import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import LoopSlider from "./LoopSlider";
import VCarPlot from "../../VirtualLoops/Plots/vCarPlot";
import { SetVirtualLoopsConfig } from "../../../utils/graphql/hooks/virtualLoopsHooks";

function ConfigManager() {
  const path = useSelector(
    (state: RootState) => state.virtualLoops?.path,
    isEqual,
  );
  const [pathInput, setPathInput] = useState(path || "");

  const cornerConfig = useSelector(
    (state: RootState) => state.virtualLoops?.cornerConfig,
    isEqual,
  );
  const [localCornerConfig, setLocalCornerConfig] = useState(
    cornerConfig || {},
  );
  const [isReordering, setIsReordering] = useState(false);

  const generation = useSelector(
    (state: RootState) => state.virtualLoops?.generation,
    isEqual,
  );
  const [generationSelect, setGenerationSelect] = useState(
    generation || "Gen3",
  );

  const configLapData = useSelector(
    (state: RootState) => state.virtualLoops?.configLapData,
    isEqual,
  );

  useEffect(() => {
    if (path) {
      setPathInput(path);
    }
  }, [path]);

  useEffect(() => {
    if (cornerConfig) {
      setLocalCornerConfig(cornerConfig);
    }
  }, [cornerConfig]);

  useEffect(() => {
    if (generation) {
      setGenerationSelect(generation);
    }
  }, [generation]);

  const setVirtualLoopsConfig = SetVirtualLoopsConfig();

  const reorderCornerConfig = () => {
    const currentEntries = Object.entries(localCornerConfig);
    const sortedEntries = [...currentEntries].sort(([, aValue], [, bValue]) => {
      if (aValue === "EOL") return 1;
      if (bValue === "EOL") return -1;
      return Number(aValue) - Number(bValue);
    });

    const orderChanged = currentEntries.some(([, v], idx) => {
      return v !== sortedEntries[idx][1];
    });

    if (orderChanged) {
      setIsReordering(true);
      setTimeout(() => {
        const sortedConfig = Object.fromEntries(sortedEntries);
        setLocalCornerConfig(sortedConfig);
        setTimeout(() => setIsReordering(false), 300);
      }, 150);
    }
  };

  const renameCorner = (oldName: string, newName: string) => {
    setLocalCornerConfig((prev) => {
      const entries = Object.entries(prev).map(([k, v]) =>
        k === oldName ? [newName, v] : [k, v],
      );
      return Object.fromEntries(entries);
    });
  };

  const changeCornerValue = (cornerName: string, newValue: string | number) => {
    setLocalCornerConfig((prev) => {
      return { ...prev, [cornerName]: newValue };
    });
  };

  const deleteCorner = (nameToDelete: string) => {
    const updatedConfig = { ...localCornerConfig };
    delete updatedConfig[nameToDelete];
    setLocalCornerConfig(updatedConfig);
  };

  const addCorner = () => {
    setLocalCornerConfig((prev) => {
      const newCornerNumbers = Object.keys(prev)
        .map((k) => {
          const match = /^New - (\d+)$/.exec(k);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n) => n !== null) as number[];
      const next =
        newCornerNumbers.length > 0 ? Math.max(...newCornerNumbers) + 1 : 1;
      return { [`New - ${next}`]: 100, ...prev };
    });
  };

  function formatCornerConfigForBackend(
    cornerConfig: Record<string, string | number>,
  ) {
    const formattedConfig = [];
    for (const cornerName in cornerConfig) {
      formattedConfig.push({
        cornerName,
        cornerValue: String(cornerConfig[cornerName]),
      });
    }
    return formattedConfig;
  }

  const reloadConfig = async () => {
    try {
      await setVirtualLoopsConfig({
        variables: {
          virtualLoopsConfig: {
            cornerConfig: formatCornerConfigForBackend(localCornerConfig),
            path: pathInput,
            generation: generationSelect,
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const resetConfig = () => {
    if (cornerConfig) {
      setLocalCornerConfig(cornerConfig);
    }
    if (path) {
      setPathInput(path);
    }
    if (generation) {
      setGenerationSelect(generation);
    }
  };

  const hasConfigData =
    configLapData &&
    "sLap" in configLapData &&
    configLapData.sLap &&
    configLapData.sLap.length > 0;

  const hasUnsavedChanges =
    !isEqual(localCornerConfig, cornerConfig) ||
    pathInput !== path ||
    generationSelect !== generation;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button onClick={reloadConfig}>Reload</button>
        {hasUnsavedChanges ? (
          <div className={styles.warning}>NOT SAVED</div>
        ) : (
          <div className={styles.saved}>SAVED</div>
        )}
      </div>
      <div className={styles.pathOptions}>
        <select
          value={generationSelect}
          onChange={(e) => setGenerationSelect(e.target.value)}
        >
          <option value="Gen3">Gen 3</option>
          <option value="Gen4">Gen 4</option>
        </select>

        <input
          className={styles.pathInput}
          type="text"
          value={pathInput}
          onChange={(e) => setPathInput(e.target.value)}
        />
      </div>

      <VCarPlot previewCornerConfig={localCornerConfig} />
      <div className={styles.buttonRow}>
        <button onClick={addCorner}>Add Corner</button>
        <button onClick={resetConfig}>Reset</button>
      </div>

      <div className={styles.slidersContainer}>
        <div className={isReordering ? styles.reordering : ""}>
          {localCornerConfig &&
            Object.entries(localCornerConfig).map(([cornerName, sLapValue]) => (
              <LoopSlider
                key={cornerName}
                name={cornerName}
                sLapValue={sLapValue}
                renameCorner={renameCorner}
                changeCornerValue={changeCornerValue}
                reorderCornerConfig={reorderCornerConfig}
                deleteCorner={deleteCorner}
                showSlider={hasConfigData}
              />
            ))}
        </div>
      </div>
    </div>
  );
}

export default ConfigManager;
